import { useRef, useEffect, useState, useCallback } from "react";
import { Bold, Italic, Underline, List, Link2, Image as ImageIcon, Heading, Maximize2, AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const readFileB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const ToolBtn = ({ title, onClick, children, testid }) => (
  <button type="button" title={title} data-testid={testid} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800">
    {children}
  </button>
);

const IMG_CLASS = "[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg";

// Lightweight rich-text editor (contentEditable) that emits HTML via onChange.
// Supports bold/italic/underline, headings, bullet lists, links, and resizable image upload.
export const RichTextEditor = ({ value = "", onChange, placeholder = "Write your update…", disabled = false, testid = "rich-editor" }) => {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  const [selImg, setSelImg] = useState(null);
  const [box, setBox] = useState(null);
  const [widthPx, setWidthPx] = useState("");
  const [altText, setAltText] = useState("");
  const [colorVal, setColorVal] = useState("#0f172a");
  const drag = useRef(null);
  const savedRange = useRef(null);

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || "";
  }, [value]);

  const emit = () => onChange?.(ref.current?.innerHTML || "");

  const measure = useCallback((img) => {
    if (!img || !wrapRef.current) { setBox(null); return; }
    const ir = img.getBoundingClientRect();
    const wr = wrapRef.current.getBoundingClientRect();
    setBox({ left: ir.left - wr.left, top: ir.top - wr.top, width: ir.width, height: ir.height });
    setWidthPx(String(Math.round(ir.width)));
  }, []);

  const selectImg = (img) => {
    setSelImg(img);
    setAltText(img ? (img.getAttribute("alt") || "") : "");
    if (img && !img.complete) img.addEventListener("load", () => measure(img), { once: true });
    measure(img);
  };

  const exec = (cmd, arg = null) => { document.execCommand(cmd, false, arg); ref.current?.focus(); emit(); };

  const addLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (!url) return;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    exec("createLink", href);
  };

  const setColor = (color) => {
    const range = savedRange.current;
    if (!range || range.collapsed) return;
    const span = document.createElement("span");
    span.style.color = color;
    try { span.appendChild(range.extractContents()); range.insertNode(span); }
    catch { return; }
    const sel = window.getSelection();
    sel.removeAllRanges();
    const nr = document.createRange();
    nr.selectNodeContents(span);
    sel.addRange(nr);
    savedRange.current = nr.cloneRange();
    emit();
  };

  const alignImg = (mode) => {
    if (!selImg) return;
    selImg.style.cssText = { left: "float:left", right: "float:right", center: "display:block;margin:0 auto" }[mode] || "";
    measure(selImg);
    emit();
  };
  const align = (mode) => {
    if (selImg) { alignImg(mode); return; }
    exec({ left: "justifyLeft", center: "justifyCenter", right: "justifyRight" }[mode]);
  };

  const addImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image exceeds the 8 MB limit"); return; }
    try {
      const data = await readFileB64(file);
      const { data: res } = await api.post("/news/image", { filename: file.name, content_type: file.type || "image/png", data });
      ref.current?.focus();
      document.execCommand("insertHTML", false, `<img src="${res.url}" alt="${file.name}" />`);
      emit();
      toast.success("Image added — click it to resize");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Image upload failed"); }
  };

  const applyWidth = (px) => {
    if (!selImg) return;
    if (px == null || px === "") { selImg.removeAttribute("width"); selImg.removeAttribute("height"); }
    else { selImg.setAttribute("width", String(Math.round(px))); selImg.removeAttribute("height"); }
    measure(selImg);
    emit();
  };

  const onWidthInput = (v) => {
    setWidthPx(v);
    const n = parseInt(v, 10);
    if (v === "") applyWidth(null);
    else if (!Number.isNaN(n) && n >= 20) applyWidth(n);
  };

  // Drag-to-resize via the corner handle.
  const startDrag = (e) => {
    e.preventDefault();
    if (!selImg) return;
    const maxW = ref.current ? ref.current.clientWidth - 8 : 800;
    drag.current = { startX: e.clientX, startW: selImg.getBoundingClientRect().width, maxW };
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", endDrag, { once: true });
  };
  const onDrag = (e) => {
    if (!drag.current || !selImg) return;
    let w = drag.current.startW + (e.clientX - drag.current.startX);
    w = Math.max(40, Math.min(drag.current.maxW, w));
    selImg.setAttribute("width", String(Math.round(w)));
    selImg.removeAttribute("height");
    measure(selImg);
  };
  const endDrag = () => {
    window.removeEventListener("mousemove", onDrag);
    drag.current = null;
    emit();
  };

  const onContentClick = (e) => {
    if (e.target && e.target.tagName === "IMG") selectImg(e.target);
    else setSelImg(null);
  };

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setSelImg(null); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const PRESETS = [["S", 200], ["M", 360], ["L", 560]];
  const SWATCHES = ["#5dbf0d", "#0f172a", "#dc2626", "#2563eb", "#d97706", "#7c3aed"];

  return (
    <div ref={wrapRef} className="relative rounded-lg border border-slate-200" data-testid={testid}>
      {!disabled && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-100 px-1.5 py-1">
          <ToolBtn title="Heading" testid={`${testid}-h`} onClick={() => exec("formatBlock", "<h3>")}><Heading className="h-4 w-4" /></ToolBtn>
          <ToolBtn title="Bold" testid={`${testid}-bold`} onClick={() => exec("bold")}><Bold className="h-4 w-4" /></ToolBtn>
          <ToolBtn title="Italic" testid={`${testid}-italic`} onClick={() => exec("italic")}><Italic className="h-4 w-4" /></ToolBtn>
          <ToolBtn title="Underline" testid={`${testid}-underline`} onClick={() => exec("underline")}><Underline className="h-4 w-4" /></ToolBtn>
          <ToolBtn title="Bulleted list" testid={`${testid}-ul`} onClick={() => exec("insertUnorderedList")}><List className="h-4 w-4" /></ToolBtn>
          <ToolBtn title="Align left" testid={`${testid}-align-left`} onClick={() => align("left")}><AlignLeft className="h-4 w-4" /></ToolBtn>
          <ToolBtn title="Align center" testid={`${testid}-align-center`} onClick={() => align("center")}><AlignCenter className="h-4 w-4" /></ToolBtn>
          <ToolBtn title="Align right" testid={`${testid}-align-right`} onClick={() => align("right")}><AlignRight className="h-4 w-4" /></ToolBtn>
          <label title="Text colour" data-testid={`${testid}-color-label`} onMouseDown={() => saveRange()} className="relative flex h-8 w-8 cursor-pointer items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800">
            <span className="text-sm font-bold leading-none">A</span>
            <span className="absolute bottom-1 h-1 w-4 rounded-full" style={{ backgroundColor: colorVal }} />
            <input type="color" value={colorVal} onMouseDown={(e) => { e.stopPropagation(); saveRange(); }} onChange={(e) => { setColorVal(e.target.value); setColor(e.target.value); }} data-testid={`${testid}-color`} className="absolute inset-0 cursor-pointer opacity-0" />
          </label>
          <div className="flex items-center gap-1 pl-0.5">
            {SWATCHES.map((c) => (
              <button key={c} type="button" title={`Colour ${c}`} data-testid={`${testid}-swatch-${c.replace("#", "")}`}
                onMouseDown={(e) => { e.preventDefault(); saveRange(); }} onClick={() => { setColorVal(c); setColor(c); }}
                className="h-4 w-4 rounded-full border border-slate-200 transition-transform hover:scale-110" style={{ backgroundColor: c }} />
            ))}
          </div>
          <ToolBtn title="Insert link" testid={`${testid}-link`} onClick={addLink}><Link2 className="h-4 w-4" /></ToolBtn>
          <label title="Upload image" data-testid={`${testid}-image-label`} className="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800">
            <ImageIcon className="h-4 w-4" />
            <input type="file" accept="image/*" className="hidden" onChange={addImage} data-testid={`${testid}-image`} />
          </label>
        </div>
      )}

      {!disabled && selImg && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-primary/5 px-2 py-1.5 text-xs text-slate-600" data-testid={`${testid}-img-tools`}>
          <span className="font-medium text-slate-500">Image width</span>
          <input type="number" min="20" value={widthPx} onChange={(e) => onWidthInput(e.target.value)} data-testid={`${testid}-img-width`}
            className="h-7 w-20 rounded border border-slate-200 px-2 text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary/40" />
          <span className="text-slate-400">px</span>
          <div className="flex items-center gap-1">
            {PRESETS.map(([label, w]) => (
              <button key={label} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyWidth(w)} data-testid={`${testid}-img-preset-${label.toLowerCase()}`}
                className="rounded border border-slate-200 bg-white px-2 py-0.5 font-medium text-slate-600 hover:border-primary/40 hover:text-primary">{label}</button>
            ))}
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyWidth(null)} data-testid={`${testid}-img-preset-full`}
              className="flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-0.5 font-medium text-slate-600 hover:border-primary/40 hover:text-primary"><Maximize2 className="h-3 w-3" /> Fit</button>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-slate-500">Alt text</span>
            <input type="text" value={altText} onChange={(e) => { setAltText(e.target.value); if (selImg) { selImg.setAttribute("alt", e.target.value); emit(); } }} data-testid={`${testid}-img-alt`}
              placeholder="Describe the image" className="h-7 w-48 rounded border border-slate-200 px-2 text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary/40" />
          </div>
        </div>
      )}

      <div
        ref={ref}
        contentEditable={!disabled}
        onInput={emit}
        onClick={onContentClick}
        onMouseUp={saveRange}
        onKeyUp={saveRange}
        data-placeholder={placeholder}
        data-testid={`${testid}-content`}
        className={`prose prose-sm max-w-none min-h-[120px] px-3 py-2 text-sm text-slate-700 focus:outline-none [&:empty]:before:text-slate-400 [&:empty]:before:content-[attr(data-placeholder)] [&_a]:text-primary [&_a]:underline ${IMG_CLASS}`}
        suppressContentEditableWarning
      />

      {!disabled && selImg && box && (
        <>
          <div className="pointer-events-none absolute z-10 rounded ring-2 ring-primary/70" style={{ left: box.left, top: box.top, width: box.width, height: box.height }} />
          <div onMouseDown={startDrag} data-testid={`${testid}-img-handle`} title="Drag to resize"
            className="absolute z-20 h-3.5 w-3.5 cursor-nwse-resize rounded-sm border-2 border-white bg-primary shadow"
            style={{ left: box.left + box.width - 7, top: box.top + box.height - 7 }} />
        </>
      )}
    </div>
  );
};
